import { ROLE_LABEL, Shell } from "@/app/Shell";
import { TabBar } from "@/app/TabBar";
import { useDashboard } from "@/app/queries";
import { useMe } from "@/app/session";
import { EmptyState, SkeletonRows, StateCard } from "@/ui/states";
import { DeadlinesCard } from "./dashboard/DeadlinesCard";
import { MobileDashboard } from "./dashboard/MobileDashboard";
import { NowSection } from "./dashboard/NowSection";
import { TracksRail } from "./dashboard/TracksRail";
import { WaitingWidget } from "./dashboard/WaitingWidget";
import { headerMeta } from "./dashboard/derive";
import { useIsMobile } from "./dashboard/useIsMobile";

/** «Мой маршрут» — S1 (вариант 1a) на десктопе, S8 на телефоне. */
export function DashboardPage() {
  const me = useMe();
  const dash = useDashboard();
  const mobile = useIsMobile();
  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;

  if (dash.isPending || me.isPending) {
    return (
      <Shell nav user={user}>
        <SkeletonRows />
        <TabBar />
      </Shell>
    );
  }
  if (dash.isError) {
    return (
      <Shell nav user={user}>
        <StateCard
          code="ERR_NETWORK"
          where="студент · мой маршрут"
          tone="warn"
          title="Нет связи с сервером"
          body="Маршрут не загрузился. Отметки и гейты не потеряны — они на сервере; повтори запрос, когда связь вернётся."
          primary={{ label: "повторить сейчас", onClick: () => void dash.refetch() }}
        />
        <TabBar />
      </Shell>
    );
  }
  const data = dash.data;
  const { route, current, deadlines, cohort } = data;
  const meta = headerMeta(data);

  if (mobile) {
    return (
      <Shell nav user={user} meta={meta}>
        <MobileDashboard data={data} />
        <TabBar />
      </Shell>
    );
  }

  const track = route.active_track;
  return (
    <Shell nav user={user} meta={meta}>
      <div>
        <div className="mb-3 font-mono text-[11px] uppercase tracking-[.08em] text-accdim">студент · {cohort ?? "вне когорты"}</div>
        <h1 className="m-0 font-mono text-[34px] font-bold leading-[1.1] tracking-[-.02em] text-ink">Мой маршрут</h1>
      </div>
      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_336px] md:gap-8">
        <div className="flex min-w-0 flex-col gap-5">
          {current && track ? (
            <NowSection current={current} track={track} />
          ) : route.active ? (
            <EmptyState
              where="студент · сейчас"
              title="Шаг ещё готовится"
              body={`Материалы шага «${route.active.title}» пока не опубликованы — админ соберёт их в конструкторе, и шаг откроется здесь.`}
            />
          ) : (
            <EmptyState where="студент · мой маршрут" title="Маршрут пройден" body="Все гейты сданы. Новые шаги появятся, когда админ добавит их в курс." />
          )}

          <section aria-labelledby="subs-title">
            <h2 id="subs-title" className="m-0 mb-3 font-mono text-[13px] font-semibold uppercase tracking-[.04em] text-mut">
              мои сдачи
            </h2>
            <EmptyState where="студент · мои сдачи" title="Сдач пока нет" body="Первая появится, когда отправишь ноутбук ментору." />
          </section>

          <section className="grid items-start gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
            <DeadlinesCard deadlines={deadlines} cohort={cohort} />
            <EmptyState where="студент · ответы ментора" title="Ответов пока нет" body="Комментарии к ячейкам появятся после первой проверки." />
          </section>
        </div>
        <div className="flex min-w-0 flex-col gap-6">
          <TracksRail route={route} />
          <WaitingWidget current={current} />
        </div>
      </div>
      <TabBar />
    </Shell>
  );
}
